import React from 'react';
import type { ValidationError } from '@/utils/validation/types';
import { InvoiceConfigForm } from '@/components/config_forms/invoice/InvoiceConfigForm';
import { sortReminderDays, updateReminderDayAtIndex } from '@/components/config_forms/invoice/utils/reminderDays';
import { focusNewReminderChip } from '@/components/config_forms/invoice/utils/focusManagement';
import type { ClientFormData } from '../new_client/utilities/formDataUtils';
import { getFieldErrorElement } from '@/utils/validation/helpers';

interface ClientInvoiceConfigProps {
  formData: ClientFormData;
  handleChange: (field: keyof ClientFormData, value: ClientFormData[keyof ClientFormData]) => void;
  handleSwitchChange: (field: keyof ClientFormData, checked: boolean) => void;
  onFieldBlur?: (field: keyof ClientFormData) => void;
  isLoading?: boolean;
  errors?: ValidationError[];
  touchedFields?: Set<string>;
  isCustomized?: boolean;
  onRevert?: () => void;
}

export const ClientInvoiceConfig: React.FC<ClientInvoiceConfigProps> = ({
  formData,
  handleChange,
  handleSwitchChange,
  onFieldBlur,
  isLoading,
  errors = [],
  touchedFields = new Set(),
  isCustomized,
  onRevert,
}) => {
  const {
    payment_terms_days,
    use_late_fee,
    late_fee_type,
    late_fee_percentage,
    late_fee_fixed_amount,
    recurring_late_fee,
    recurring_late_fee_interval,
    use_early_payment_discount,
    use_cancellation_policy,
    auto_send_reminders,
    reminder_days,
    employment_type,
  } = formData;

  // Reminder logic
  const sortedReminderDays = sortReminderDays(reminder_days || []);

  const handleRemoveReminderDay = (index: number) => {
    const newDays = (sortedReminderDays || []).filter((_, i) => i !== index);
    handleChange('reminder_days', newDays);
  };

  const handleUpdateReminderDay = (idx: number, newValue: number | null) => {
    const newDays = updateReminderDayAtIndex(sortedReminderDays, idx, newValue);
    handleChange('reminder_days', newDays);
  };

  const handleAddEmptyReminder = () => {
    const newChipVisualIndex = sortedReminderDays.length;
    handleChange('reminder_days', [...sortedReminderDays, 0]);
    focusNewReminderChip(newChipVisualIndex);
  };

  const handleChipValidationComplete = (idx: number, isValid: boolean, validatedValue: number | null, originalValueBeforeEdit: number) => {
    if (originalValueBeforeEdit === 0 && !isValid && (validatedValue === null || validatedValue === 0)) {
      handleRemoveReminderDay(idx);
    }
  };

  return (
    <InvoiceConfigForm
      title="Invoice Configuration"
      description="Configure invoice settings and payment terms for this client."
      accountsPayableEmailProps={{
        value: formData.accounts_payable_email ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('accounts_payable_email', e.target.value || null),
        onBlur: () => onFieldBlur?.('accounts_payable_email'),
        disabled: isLoading,
        'data-validation-key': 'accounts_payable_email',
      }}
      showAccountsPayableEmail={true}
      paymentTermsDays={payment_terms_days ?? null}
      paymentTermsProps={{
        value: payment_terms_days ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('payment_terms_days', e.target.value ? parseInt(e.target.value) : null),
        onBlur: () => onFieldBlur?.('payment_terms_days'),
        disabled: isLoading,
      }}
      accountsPayableEmailError={getFieldErrorElement('accounts_payable_email', errors, touchedFields)}
      paymentTermsError={getFieldErrorElement('payment_terms_days', errors, touchedFields)}
      useLateFee={use_late_fee ?? false}
      lateFeeType={(late_fee_type as 'fixed' | 'percentage') || 'percentage'}
      recurringLateFee={recurring_late_fee || 'once'}
      onToggleLateFee={(checked) => handleSwitchChange('use_late_fee', checked)}
      onLateFeeTypeChange={(type) => {
        handleChange('late_fee_type', type);
      }}
      onRecurringLateFeeChange={(value) => {
        handleChange('recurring_late_fee', value);
      }}
      onIsRecurringLateFeeChange={(checked) => {
        handleChange('recurring_late_fee', checked ? 'daily' : 'once');
      }}
      lateFeePctProps={{
        value: formData.late_fee_percentage ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('late_fee_percentage', e.target.value ? parseFloat(e.target.value) : null),
        onBlur: () => onFieldBlur?.('late_fee_percentage'),
        disabled: isLoading,
      }}
      lateFeeFixedProps={{
        value: formData.late_fee_fixed_amount ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('late_fee_fixed_amount', e.target.value ? parseFloat(e.target.value) : null),
        onBlur: () => onFieldBlur?.('late_fee_fixed_amount'),
        disabled: isLoading,
      }}
      recurringIntervalProps={{
        value: formData.recurring_late_fee_interval ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('recurring_late_fee_interval', e.target.value ? parseInt(e.target.value) : null),
        onBlur: () => onFieldBlur?.('recurring_late_fee_interval'),
        disabled: isLoading,
      }}
      lateFeePctError={getFieldErrorElement('late_fee_percentage', errors, touchedFields)}
      lateFeeFixedError={getFieldErrorElement('late_fee_fixed_amount', errors, touchedFields)}
      recurringIntervalError={getFieldErrorElement('recurring_late_fee_interval', errors, touchedFields)}
      useEarlyPaymentDiscount={use_early_payment_discount ?? false}
      earlyPaymentType={(formData.early_payment_type as 'fixed' | 'percentage') || 'percentage'}
      onToggleEarlyPayment={(checked) => handleSwitchChange('use_early_payment_discount', checked)}
      onEarlyPaymentTypeChange={(type) => {
        handleChange('early_payment_type', type);
      }}
      earlyDiscountPctProps={{
        value: formData.early_payment_discount_percentage ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('early_payment_discount_percentage', e.target.value ? parseFloat(e.target.value) : null),
        onBlur: () => onFieldBlur?.('early_payment_discount_percentage'),
        disabled: isLoading,
      }}
      earlyDiscountFixedProps={{
        value: formData.early_payment_fixed_amount ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('early_payment_fixed_amount', e.target.value ? parseFloat(e.target.value) : null),
        onBlur: () => onFieldBlur?.('early_payment_fixed_amount'),
        disabled: isLoading,
      }}
      earlyDaysProps={{
        value: formData.early_payment_days ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('early_payment_days', e.target.value ? parseInt(e.target.value) : null),
        onBlur: () => onFieldBlur?.('early_payment_days'),
        disabled: isLoading,
      }}
      earlyDiscountPctError={getFieldErrorElement('early_payment_discount_percentage', errors, touchedFields)}
      earlyDiscountFixedError={getFieldErrorElement('early_payment_fixed_amount', errors, touchedFields)}
      earlyDaysError={getFieldErrorElement('early_payment_days', errors, touchedFields)}
      useCancellationPolicy={use_cancellation_policy ?? false}
      cancellationPolicy={Array.isArray(formData.cancellation_policy) ? formData.cancellation_policy as { days: number | null; type: "percentage" | "fixed"; value: number | null }[] : []}
      onToggleCancellationPolicy={(checked) => handleSwitchChange('use_cancellation_policy', checked)}
      onCancellationPolicyChange={(newRules) => handleChange('cancellation_policy', newRules)}
      onDeactivateCancellationPolicy={() => handleSwitchChange('use_cancellation_policy', false)}
      rushPremiumPolicy={Array.isArray(formData.rush_premium_policy) ? formData.rush_premium_policy as unknown as import('@/types').RushPremiumTier[] : []}
      onRushPremiumPolicyChange={(newRules) => handleChange('rush_premium_policy', newRules)}
      autoSendReminders={auto_send_reminders ?? false}
      reminderDays={sortedReminderDays}
      validationKey="client-reminder-days"
      onToggleAutoSendReminders={(checked) => handleSwitchChange('auto_send_reminders', checked)}
      onUpdateReminderDay={handleUpdateReminderDay}
      onValidationComplete={handleChipValidationComplete}
      onRemoveReminderDay={handleRemoveReminderDay}
      onAddEmptyReminder={handleAddEmptyReminder}
      onClearGroupError={() => {}} // No-op for client form
      employmentType={employment_type || '1099'}
      showEmploymentType={true}
      isCustomized={isCustomized}
      onRevert={onRevert}
      context="client"
    />
  );
};
